import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PaymentStatus } from '@/types/api/payment';
import { usePollPaymentQuery } from './queries/use-poll-payment-query';
import useModalStore from '@/stores/modal.store';
import { ModalType } from '@/types/stores/modal';
import config from '@/config/index';
import useGlobalStore from '@/stores/global.store';
import { useCancelPayment } from './use-cancel-payment';

export default function usePollPayment() {
  const [polling, setPolling] = React.useState<boolean>(false);
  const statusRef = React.useRef<PaymentStatus | undefined>(undefined);

  const queryClient = useQueryClient();
  const openModal = useModalStore((state) => state.open);
  const closeModal = useModalStore((state) => state.close);
  const orderId = useGlobalStore((state) => state.orderId);
  const paymentId = useGlobalStore((state) => state.paymentId);
  const stopPayment = useGlobalStore((state) => state.stopPayment);

  const { data: payment } = usePollPaymentQuery(paymentId, polling, config.app.paymentflow.pollInterval);
  const { cancel: cancelPayment } = useCancelPayment();

  const handleFailure = React.useCallback(
    (message: string) => {
      stopPayment();
      setPolling(false);
      openModal(ModalType.PaymentFailed, { message });
    },
    [setPolling, openModal],
  );

  React.useEffect(() => {
    if (!payment) return;

    statusRef.current = payment.status;

    switch (payment.status) {
      case PaymentStatus.Initiating:
      case PaymentStatus.Pending:
        setPolling(true);

        openModal(ModalType.PaymentPending, {
          onClose: () => {
            if (statusRef.current === PaymentStatus.Pending || statusRef.current === PaymentStatus.Initiating) {
              cancelPayment();
            }
          },
        });

        break;

      case PaymentStatus.Expired: // server timeout
        handleFailure('Betalning tog för lång tid.');
        break;

      case PaymentStatus.Failed:
        handleFailure('Någonting gick snett.');
        break;

      case PaymentStatus.Cancelled:
        setPolling(false);
        stopPayment();
        closeModal();
        break;

      case PaymentStatus.Completed:
        setPolling(false);
        // The order's own paymentStatus reflects this once refetched — usePollOrderConfirmation
        // picks up from there instead of a manually-synced flow flag.
        queryClient.invalidateQueries({ queryKey: ['orders', orderId] });
        openModal(ModalType.OrderConfirming);
        break;
    }

    return () => {
      setPolling(false);
    };
  }, [payment?.status, setPolling, openModal]);
}
